import { useCallback, useEffect, useRef, useState } from "react";
import lottoApi from "./api";
import { useAuth } from "../contexts/JWTContext";

/*
 * กิจกรรมการตลาด (อ่านบทความ / ดูคลิป ตอบคำถามรับรางวัล) — ตรรกะฝั่งลูกค้า (ใช้ร่วมกันทั้ง landing และ landing-ver2; ไฟล์นี้เหมือนกันทั้งสองแอป)
 * หน้าตาแยกกันที่ pages/MarketingActivity.js (landing = บอร์ดใบปลิวกิจกรรม · landing-ver2 = แดชบอร์ดภารกิจนีออน)
 *   • เซิร์ฟเวอร์ตัดสินทุกอย่าง (ตอบถูกไหม ได้รางวัลเท่าไหร่ ความถี่ โควต้า ตอบผิดได้กี่ครั้ง เปิด-ปิดระบบ) และไม่ส่งเฉลยมาที่หน้านี้
 *     ฝั่งนี้แค่แสดงผลและส่งคำตอบ
 */

const nf = (v) => Number(v || 0).toLocaleString("th-TH", { maximumFractionDigits: 2 });
export const fmtBaht = (v) => `฿${nf(v)}`;

export const KIND_META = {
  ARTICLE: { label: "บทความ", verb: "อ่านบทความ", icon: "fluent-emoji-flat:open-book", tone: "sky" },
  VIDEO: { label: "คลิปวิดีโอ", verb: "ดูคลิป", icon: "fluent-emoji-flat:clapper-board", tone: "rose" },
  OTHER: { label: "กิจกรรม", verb: "ร่วมกิจกรรม", icon: "fluent-emoji-flat:megaphone", tone: "amber" },
};
export const kindInfo = (k) => KIND_META[k] || KIND_META.OTHER;

/** รางวัลต่อครั้ง → { text: "5 เพชร" | "฿20", icon, cash } */
export function rewardInfo(a) {
  if (!a || !(Number(a.reward_amount) > 0)) return { text: "ไม่มีรางวัล", icon: "fluent-emoji-flat:party-popper", cash: false, none: true };
  return a.reward_type === "CASH"
    ? { text: fmtBaht(a.reward_amount), icon: "fluent-emoji-flat:money-bag", cash: true }
    : { text: `${nf(a.reward_amount)} เพชร`, icon: "fluent-emoji-flat:gem-stone", cash: false };
}

/** สถานะสิทธิ์ของฉันในกิจกรรม → { label, tone: open|done|locked|full, canDo, hint } */
export function stateInfo(a) {
  switch (a && a.state) {
    case "DONE":
      return { label: "ทำครบแล้ว", tone: "done", canDo: false, hint: "คุณรับรางวัลกิจกรรมนี้แล้ว" };
    case "DONE_TODAY":
      return { label: "วันนี้ทำแล้ว", tone: "done", canDo: false, hint: "กลับมาทำใหม่ได้พรุ่งนี้" };
    case "FULL":
      return { label: "สิทธิ์เต็มแล้ว", tone: "full", canDo: false, hint: "สิทธิ์รางวัลถูกรับครบแล้ว" };
    case "LOCKED":
      return { label: "ตอบผิดครบกำหนด", tone: "locked", canDo: false, hint: a.frequency === "ONCE" ? "ไม่สามารถทำกิจกรรมนี้ต่อได้" : "ลองใหม่ได้พรุ่งนี้" };
    default:
      return { label: "ร่วมกิจกรรม", tone: "open", canDo: true, hint: "" };
  }
}

export const freqText = (a) => (a.frequency === "ONCE" ? "รับได้ครั้งเดียว" : a.frequency === "DAILY" ? "รับได้วันละครั้ง" : "รับได้ทุกครั้ง");

/** เวลาที่เหลือของกิจกรรม (วินาทีจากเซิร์ฟเวอร์) → "เหลือ 2 วัน" / "เหลือ 5 ชม." / "เหลือ 12 นาที" ; ไม่มีกำหนด = "" */
export function leftText(a) {
  const s = a && a.seconds_left;
  if (s === null || s === undefined) return "";
  if (s >= 86400) return `เหลือ ${Math.ceil(s / 86400)} วัน`;
  if (s >= 3600) return `เหลือ ${Math.ceil(s / 3600)} ชม.`;
  return `เหลือ ${Math.max(1, Math.ceil(s / 60))} นาที`;
}
export const isUrgent = (a) => !!a && a.seconds_left !== null && a.seconds_left !== undefined && a.seconds_left < 86400;

/** รูปปกกิจกรรม (path จากเซิร์ฟเวอร์ เช่น /activity-assets/megaphone.svg หรือ /uploads/x.png) → URL เต็ม */
export const coverImg = (url) => {
  const u = String(url || "");
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return `${lottoApi.base || ""}${u.startsWith("/") ? u : `/${u}`}`;
};

export const turnPct = (a) => (a && a.turnover_required > 0 ? Math.max(0, Math.min(100, (a.turnover_done / a.turnover_required) * 100)) : 0);

/** draft = { [questionId]: "คำตอบ" } → body ของ API (ตัดช่องว่างหัวท้าย) */
export const answersPayload = (questions, draft) => (questions || []).map((q) => ({ questionId: q.id, answer: String((draft && draft[q.id]) || "").trim() }));

/** ตอบครบทุกข้อแล้วหรือยัง (ไม่มีคำถาม = ส่งได้เลย) */
export const draftComplete = (questions, draft) => (questions || []).every((q) => String((draft && draft[q.id]) || "").trim() !== "");

export { whenText } from "./creditCodeLogic";

const EMPTY = { enabled: true, activities: [], history: [], active: [], totals: { count: 0, cash: 0, diamonds: 0 }, seconds_to_reset: 0 };

export function useMarketingActivity(onDone) {
  const { user, isAuthenticated } = useAuth();
  const uid = isAuthenticated ? user?.id : null;
  const [view, setView] = useState(null); // null = ยังโหลดไม่เสร็จ
  const [busyId, setBusyId] = useState(null);
  const [result, setResult] = useState(null); // { kind:"win", title, reward } | { kind:"wrong", title, attempts_left } | { kind:"error", message }
  const alive = useRef(true);
  const inFlight = useRef(false);
  const cb = useRef(onDone);
  cb.current = onDone;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(() => {
    if (!uid) return Promise.resolve();
    return Promise.resolve()
      .then(() => lottoApi.myActivities())
      .then((v) => alive.current && setView({ ...EMPTY, ...(v || {}) }))
      .catch(() => alive.current && setView((prev) => prev || { ...EMPTY, failed: true }));
  }, [uid]);

  useEffect(() => {
    setView(null);
    setResult(null);
    load();
    const onVis = () => !document.hidden && load();
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [load]);

  /** ส่งคำตอบ → คืน true เมื่อตอบถูก (ให้หน้าปิดแผงทำกิจกรรม) */
  const submit = useCallback(
    async (activity, draft) => {
      if (inFlight.current || !activity) return false;
      inFlight.current = true;
      setBusyId(activity.id);
      let correct = false;
      try {
        const res = await lottoApi.submitActivity(activity.id, answersPayload(activity.questions, draft));
        if (res && res.correct) {
          correct = true;
          if (alive.current) setResult({ kind: "win", title: activity.title, reward: res.reward || {} });
          if (cb.current) cb.current(res.reward);
        } else if (alive.current) {
          setResult({ kind: "wrong", title: activity.title, attempts_left: res ? res.attempts_left : null, message: (res && res.message) || "ตอบไม่ถูกต้อง" });
        }
      } catch (err) {
        const d = err?.response?.data;
        if (alive.current) setResult({ kind: "error", code: d?.code || "ERROR", message: d?.message || "ส่งคำตอบไม่สำเร็จ ลองใหม่อีกครั้ง" });
      } finally {
        inFlight.current = false;
        if (alive.current) setBusyId(null);
        load();
      }
      return correct;
    },
    [load],
  );

  const v = view || EMPTY;
  return {
    isAuthenticated,
    loading: !!uid && view === null,
    failed: !!(view && view.failed),
    view: v,
    enabled: v.enabled !== false,
    busyId,
    submit,
    result,
    closeResult: () => setResult(null),
    reload: load,
  };
}
